// Late Joiner boost: sold for diamonds, ×10 on trainer and creature kill XP until the trainer reaches a
// target level, so a late joiner can catch up to the server's players. It ends by itself at the target
// (no timer), one at a time.
//
// Target: the median trainer level of players active in the last 7 days whose accounts are 30+ days old,
// admin and dev accounts left out, recomputed with the leaderboards (server/boards.ts). Under 20 such
// players the boost is not sold. The target is frozen at purchase; if the median later rises, buying
// again after reaching it pays only the new stretch (the price is the share of the target's XP still
// missing).
//
// Price: max(minPrice, ceil(fullPrice × (targetXP − yourXP) / targetXP)) diamonds.
// While it runs a knockout costs no trainer XP.
//
// Evidence: the reference's Late Joiner Boost (pokeidle 03-hunts-xp-progression.md §8, lines 269-293):
// same median rule, ×10 on both XP tracks, same price formula, crossing kill split.
import { CONFIG } from './config.ts';
import { totalXp } from './xp.ts';

const DAY = 86_400_000;
const LJ = CONFIG.lateJoiner;

/** The diamond-shop offer id. */
export const LATE_JOINER = 'late-joiner';

/** Median of levels (the mean of the two middle ones, rounded down, for an even count); null for none. */
export function medianLevel(levels: readonly number[]): number | null {
  if (!levels.length) return null;
  const s = [...levels].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : Math.floor((s[m - 1] + s[m]) / 2);
}

export interface MedianPlayer { level: number; created: number; lastActive: number; out?: boolean }

/** Whether a player counts toward the median now: not admin/dev, active in the last week, account 30+ days old. */
export function countsForMedian(p: MedianPlayer, now: number): boolean {
  return !p.out && now - p.lastActive <= LJ.activeDays * DAY && now - p.created >= LJ.veteranDays * DAY;
}

/** Today's target: the median of the eligible players' levels, or 0 (not sold) with fewer than minPlayers. */
export interface LateJoinerTarget { target: number; n: number }

export function lateJoinerTarget(eligibleLevels: readonly number[]): LateJoinerTarget {
  const n = eligibleLevels.length;
  return { target: n >= LJ.minPlayers ? medianLevel(eligibleLevels)! : 0, n };
}

/** A trainer's total XP from level 1. */
export function trainerTotalXp(t: { level: number; xp: number }): number {
  return totalXp(t.level) + Math.max(0, t.xp);
}

/** Diamond price to boost a trainer to `target`; null when not sold (no target, or already there). */
export function lateJoinerPrice(t: { level: number; xp: number }, target: number): number | null {
  if (!(target > 1)) return null;
  const goal = totalXp(target), have = trainerTotalXp(t);
  if (have >= goal) return null;
  return Math.max(LJ.minPrice, Math.ceil((LJ.fullPrice * (goal - have)) / goal));
}

/** Whether a bought boost is still running for this trainer. */
export function lateJoinerOn(t: { level: number; lateJoiner?: { target: number } }): boolean {
  return !!t.lateJoiner && t.level < t.lateJoiner.target;
}

/**
 * Kill XP with the boost: `tr` and `cr` are the kill's trainer and creature XP after every other boost.
 * Below the target both are ×mult. On the kill that crosses it (the reference's ganhoAteOAlvo), the boosted
 * part covers only the XP still missing and the rest of the kill pays without the boost.
 */
export function lateJoinerXp(tr: number, cr: number, t: { level: number; xp: number }, target: number): { tr: number; cr: number } {
  const m = LJ.mult;
  const missing = totalXp(target) - trainerTotalXp(t);
  if (missing <= 0) return { tr, cr };
  const full = tr * m;
  if (full <= missing) return { tr: full, cr: cr * m };
  const f = missing / full;
  return { tr: Math.round(missing + tr * (1 - f)), cr: Math.round(cr * m * f + cr * (1 - f)) };
}
